import { useState } from 'react';
import loadable from '@loadable/component';
import { useSEO, useSummaryV2 } from '@/hooks/useCompany';
import { SectionWrapper } from '../../styled';
import DocumentSchema, {
  SWSOrganizationSchema,
} from '@/components/DocumentSchema';
import type { WebPage } from 'schema-dts';
import { shallowEqual, useSelector } from 'react-redux';
import { getInformationSelector } from '@/pages/CompanyReport/components/Information/redux/selectors';
import { SWSOrganizationAuthorSchema } from '@/components/DocumentSchema/DocumentSchema';

import { SectionSchema } from '@/pages/CompanyReport/schemas';
import useCompanyVariant from '@/pages/CompanyReport/hooks/useCompanyVariant';
import useUser from '@/hooks/useUser';
import { useFlag } from '@/features/unleash';

const Summary = loadable(() => import('./components/Summary'));

// Used to return peers for ASX companies
const PeerCompanies = loadable(
  () =>
    import('@/pages/CompanyReport/components/SimilarCompanies/PeerCompanies')
);

const PeerCompaniesTileExperiment = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/SimilarCompanies/PeerCompaniesTileExperiment'
    )
);

const Fundamentals = loadable(
  () =>
    import('@/pages/CompanyReport/components/Overview/components/Fundamentals'),
  {
    resolveComponent: (component) => component.Fundamentals,
  }
);

const PricePerformance = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Overview/components/PricePerformance'
    ),
  { resolveComponent: (components) => components.PricePerformance }
);

const AboutTheCompanySection = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/OverviewSection/components/AboutTheCompanySection'
    )
);

const MyNotes = loadable(
  () => import('@/pages/CompanyReport/components/MyNotes')
);

const AutoCompanyNoteModal = loadable(
  () => import('@/pages/CompanyReport/components/AutoCompanyNoteModal')
);

const CompanyNarratives = loadable(
  () => import('@/pages/CompanyReport/components/CompanyNarratives')
);

const OverviewSection = () => {
  const [showAboutCompany, setShowAboutCompany] = useState(false);
  const summary = useSummaryV2();
  const name = summary.status === 'success' ? summary.name : '';
  const uniqueSymbol = summary.status === 'success' ? summary.uniqueSymbol : '';
  const companyId =
    summary.status === 'success' ? summary.companyId.toLowerCase() : '';
  const information = useSelector<
    Parameters<typeof getInformationSelector>[0],
    ReturnType<typeof getInformationSelector>
  >((state) => getInformationSelector(state, { companyId }), shallowEqual);
  const { description } = useSEO('summary');
  const urlHashSchema = useCompanyVariant(
    summary.canonicalUrl + 'section-schema'
  );
  //TODO @RIC: Remove section-schema experiment after disabling by forcing showExperimentSchema === false
  const showExperimentSchema = parseInt(urlHashSchema, 16) >= 16;
  const {
    data: { loggedIn },
  } = useUser();
  const featureFlag = useFlag({
    name: 'mono-overview-peer-companies-tile-experiment',
  });
  const isExperimentEnabled =
    featureFlag.flagsReady &&
    featureFlag.enabled &&
    featureFlag.variant.name === 'tile-experiment';

  return (
    <>
      {companyId && !showExperimentSchema && (
        <DocumentSchema<WebPage>
          item={{
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            identifier: information.canonicalUrlWithHostName,
            mainEntity: {
              '@type': 'Corporation',
              name: information.companyShortName,
              legalName: information.companyName,
              tickerSymbol: `${information.exchangeSymbol}:${information.tickerSymbol}`,
              url: information.canonicalUrlWithHostName,
              logo: information.mainHeader,
              foundingDate: `${information.yearFounded}`,
              description,
              sameAs: information.url,
              review: {
                '@type': 'Review',
                additionalType:
                  'http://www.productontology.org/id/Fundamental_analysis',
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: information.snowflakeOverallRating.toString(),
                  bestRating: '6',
                  worstRating: '0',
                  description: information.snowflakeSentence,
                },
                name: information.snowflakeSentence,
                datePublished: information.lastAnalysisUpdateISOString,
                author: SWSOrganizationAuthorSchema,
                publisher: SWSOrganizationSchema,
              },
            },
          }}
        />
      )}
      <SectionSchema section="summary" />
      <Summary handleShowAboutCompany={setShowAboutCompany} />
      {loggedIn && (
        <MyNotes companyId={summary.companyId} uniqueSymbol={uniqueSymbol} />
      )}
      {summary.companyId && uniqueSymbol && (
        <AutoCompanyNoteModal
          companyId={summary.companyId}
          uniqueSymbol={uniqueSymbol}
        />
      )}
      {summary.canonicalUrl && (
        <CompanyNarratives canonicalUrl={summary.canonicalUrl} />
      )}

      {isExperimentEnabled ? (
        <PeerCompaniesTileExperiment
          canonicalURL={summary.canonicalUrl || ''}
          name={name}
          section="summary"
        />
      ) : (
        <PeerCompanies
          canonicalURL={summary.canonicalUrl || ''}
          name={name}
          section="summary"
        />
      )}

      <SectionWrapper data-cy-id="report-section">
        <PricePerformance
          id={name}
          uniqueSymbol={uniqueSymbol}
          lookup={summary.canonicalUrl || ''}
        />
      </SectionWrapper>
      <AboutTheCompanySection
        showAboutCompany={showAboutCompany}
        handleShowAboutCompany={setShowAboutCompany}
      />
      <Fundamentals name={summary.status === 'success' ? summary.name : ''} />
    </>
  );
};

export default OverviewSection;
